import styled, { css } from 'styled-components';
import rem from 'polished/lib/helpers/rem';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
// template components
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
// images
import navyBannerDesktop from '../images/navy-banner-desktop.webp';
import navyBannerMobile from '../images/navy-banner-mobile.webp';
import tealBannerDesktop from '../images/teal-banner-desktop.webp';
import tealBannerMobile from '../images/teal-banner-mobile.webp';
import grayBannerDesktop from '../images/gray-banner-desktop.webp';
import grayBannerMobile from '../images/gray-banner-mobile.webp';
import skyBannerDesktop from '../images/sky-banner-desktop.webp';
import skyBannerMobile from '../images/sky-banner-mobile.webp';

// local data to power the "banner" image and coloration for the podcast episode cards
const bannerArray = [
  {
    desktopImage: skyBannerDesktop,
    mobileImage: skyBannerMobile,
    bgColor: Theme.colors.sky.sky,
    themeType: 'lightTheme',
  },
  {
    desktopImage: tealBannerDesktop,
    mobileImage: tealBannerMobile,
    bgColor: Theme.colors.teal.tealLight10,
    themeType: 'lightTheme',
  },
  {
    desktopImage: grayBannerDesktop,
    mobileImage: grayBannerMobile,
    bgColor: Theme.colors.neutral.grayLight10,
    themeType: 'lightTheme',
  },
  {
    desktopImage: navyBannerDesktop,
    mobileImage: navyBannerMobile,
    bgColor: Theme.colors.blue.blue,
    themeType: 'darkTheme',
  },
];

export const ColStyledBannerContainer = styled(Col)`
  ${({ theme, itemIndex }) => {
    const xlargeUp = theme.xlargeUp();
    const largeUp = theme.largeUp();
    const mediumUp = theme.mediumUp();
    const smallUp = theme.smallUp();

    const thisCardIndex = itemIndex % bannerArray.length;
    let thisHeight = rem(125);
    let thisMaxWidth = rem(125);

    if(xlargeUp){
      thisHeight = thisMaxWidth;
    }else if(largeUp){
      thisHeight = rem(140);
    }else if(mediumUp){
      thisHeight = rem(130);
    }

    if(mediumUp){
      thisMaxWidth = 'unset';
    }else if(smallUp){
      thisMaxWidth = rem(185);
    }

    return css`
      display: flex;
      justify-content: flex-end;
      align-items: flex-end;
      background-color: ${bannerArray[thisCardIndex].bgColor};
      background-image: url(${mediumUp ? bannerArray[thisCardIndex].desktopImage.toString() : bannerArray[thisCardIndex].mobileImage.toString()});
      background-size: cover;
      background-position: left 75%;
      height: ${thisHeight};
      margin-bottom: ${mediumUp ? rem(24) : rem(12)};
      margin-left: 0;
      margin-right: 0;
      max-width: ${thisMaxWidth};
    `;
  }}
`;

export const ColStyledCardContentContainer = styled(Col)`
  ${({ theme }) => {
    const mediumUp = theme.mediumUp();
    return css`
      display: flex;
      flex: 1;
      flex-direction: column;
      padding-left: ${mediumUp ? 0 : rem(12)};
      margin-left: 0;
    `;
  }}
`;

export const ColStyledDurationContainer = styled(Col)`
  display: flex;
  flex: 0;
  align-items: center;
  padding-left: ${rem(15)};
`;

export const ColStyledPlayButtonContainer = styled(Col)`
  flex: 0;
  padding: 0;
  margin-top: auto;
`;

export const ArticleStyled = styled.article`
  ${({ theme }) => {
    const mediumUp = theme.mediumUp();
    return css`
      display: flex;
      flex-direction: column;
      background-color: ${theme.colors.neutral.grayLight30};
      padding: ${mediumUp ? rem(25) : rem(20)};
      margin-bottom: ${mediumUp ? rem(50) : rem(16)};
      ${!mediumUp && 'width: 100%;'};
    `;
  }}
`;

export const DivStyledBannerTextContainer = styled.div`
  ${({ theme }) => {
    const xlargeUp = theme.xlargeUp();
    const largeUp = theme.largeUp();
    const mediumUp = theme.mediumUp();
    const smallUp = theme.smallUp();

    let thisWidth = rem(75);
    let thisBottomPadding = rem(8);

    if(xlargeUp){
      thisWidth = rem(170);
    }else if(mediumUp){
      thisWidth = rem(160);
    }else if(smallUp){
      thisWidth = rem(110);
    }

    if(xlargeUp){
      thisBottomPadding = rem(11);
    }else if(largeUp){
      thisBottomPadding = rem(16);
    }else if(mediumUp){
      thisBottomPadding = rem(12);
    }

    return css`
      display: inline-flex;
      flex-direction: column;
      width: ${thisWidth};
      padding-bottom: ${thisBottomPadding};
    `;
  }}
`;

export const LinkStyled = styled(Link)`
  ${({ theme }) => {
    const mediumUp = theme.mediumUp();
    return css`
      display: block;
      font-size: inherit;
      margin-bottom: ${mediumUp ? rem(12) : rem(8)};
    `;
  }}
`;

export const LinkStyledPlayButton = styled(Link)`
  ${({ theme }) => {
    const thisDimension = rem(50);
    const thisTriangleBorderValue = `${rem(12)} solid transparent`;
    return css`
      display: block;
      position: relative;
      height: ${thisDimension};
      width: ${thisDimension};
      background-color: ${theme.colors.blue.blueLight20};
      border-radius: 100%;
      &:hover {
        background-color: ${theme.colors.blue.blueLight10};
      }
      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-34%, -50%);
        width: 0;
        height: 0;
        border-top: ${thisTriangleBorderValue};
        border-bottom: ${thisTriangleBorderValue};
        border-left: ${rem(22)} solid ${theme.colors.neutral.white};
      }
    `;
  }}
`;

export const RowStyledEpisodeCardContent = styled(Row)`
  display: flex;
  align-content: flex-start;
`;

export const RowStyledPlayButtonContainer = styled(Row)`
  margin-top: auto;
`;

export const TypographyStyledEpisode = styled(Typography)`
  ${({ theme, itemIndex }) => {
    const smallUp = theme.smallUp();
    const thisCardIndex = itemIndex % bannerArray.length;
    return css`
      color: ${bannerArray[thisCardIndex].themeType === 'darkTheme' ? theme.colors.neutral.white : theme.colors.neutral.grayDark20};
      ${!smallUp && `font-size: ${rem(16)}`};
    `;
  }}
`;

export const TypographyStyledSeason = styled(Typography)`
  ${({ theme, itemIndex }) => {
    const mediumUp = theme.mediumUp();
    const thisCardIndex = itemIndex % bannerArray.length;
    return css`
      color: ${bannerArray[thisCardIndex].themeType === 'darkTheme' ? theme.colors.neutral.white : theme.colors.neutral.grayDark20};
      line-height: ${mediumUp ? rem(14) : rem(6)};
      padding-left: ${rem(1)};
    `;
  }}
`;

export const TypographyStyledSeasonEpisode = styled(Typography)`
  ${({ theme }) => {
    const mediumUp = theme.mediumUp();
    return css`
      padding-bottom: ${mediumUp ? rem(10) : rem(4)};
    `;
  }}
`;